import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './EditHQCommitteeMemberValidation';

import { navLinks } from '../../UseMemberAdministration';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useMemberAdministrationHQCommitteeData from '@usas/ui-web/state/memberAdministration/hQCommittee/UseMemberAdministrationHQCommitteeData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const representativeTypeOptions = [
  { id: Constants.DEFAULT_ID, name: "--" },
  { id: 1, name: "Athlete" },
  { id: 2, name: "Non-Athlete" }
];
const termLengthOptions = [
  { id: Constants.DEFAULT_ID, name: "--" },
  { id: 1, name: "1 Year" },
  { id: 2, name: "2 Years" },
  { id: 3, name: "3 Years" },
  { id: 4, name: "4 Years" },
  { id: 5, name: "5 Years" },
  { id: 6, name: "6 Years" },
  { id: 7, name: "Ex-Officio" },
  { id: 8, name: "N/A" }
];

const useEditHQCommitteeMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { USAS_ORG_UNIT_ID, HQ_COMMITTEE_ROLE_GROUP_ID, ORG_ROLE_FIELD_TERM_LENGTH_FIELD_NAME,
    ORG_ROLE_FIELD_REPRESENTATIVE_TYPE_FIELD_NAME, environmentVariableState } = useEnvironmentVariableData();
  const { memberAdministrationHQCommitteeState, putHQCommitteeMember } = useMemberAdministrationHQCommitteeData();
  const { formState, errorState, setFormData, onFormValueChanged, onValueTextPairChanged, handleSubmit, setErrors, setIsDirty, isSubmitting
  } = useForm(getInitialFormState(), submitFormCallback, validate);
  const [viewState, setViewState] = useState(getInitialViewState);

  const onSaveClicked = () => {
    setIsDirty(true);
    handleSubmit();
  };

  const onBackClicked = () => {
    navigate(navLinks.HQ_COMMITTEE);
  };

  useEffect(() => {
    if (location.state?.personId && location.state?.personOrgRoleDurationId > 0) {
      const personId = location.state.personId;
      const personOrgRoleDurationId = location.state.personOrgRoleDurationId;

      const targetPerson = memberAdministrationHQCommitteeState.arrayData.find(x => x.personId === personId);

      if (targetPerson && Array.isArray(targetPerson.orgRoles)) {
        const targetRole = targetPerson.orgRoles.find(x => x.personOrgRoleDurationId === personOrgRoleDurationId);

        if (targetRole) {
          const representativeTypeValues = getOrgRoleFieldValues(targetRole.orgRoleFields, ORG_ROLE_FIELD_REPRESENTATIVE_TYPE_FIELD_NAME);
          const termLengthValues = getOrgRoleFieldValues(targetRole.orgRoleFields, ORG_ROLE_FIELD_TERM_LENGTH_FIELD_NAME);

          setFormData({
            ...formState,
            effectiveDate: targetRole.effectiveDate ? formatDate(targetRole.effectiveDate) : '',
            expirationDate: targetRole.expirationDate ? formatDate(targetRole.expirationDate) : '',
            representativeTypeId: representativeTypeOptions.find(x => x.name === representativeTypeValues?.fieldValue)?.id || Constants.DEFAULT_ID,
            representativeTypeName: representativeTypeValues?.fieldValue || '--',
            termLengthId: termLengthOptions.find(x => x.name === termLengthValues?.fieldValue)?.id || Constants.DEFAULT_ID,
            termLengthName: termLengthValues?.fieldValue || '--'
          });

          setViewState({
            ...viewState,
            orgRoleName: targetRole.orgRoleName,
            orgRoleId: targetRole.orgRoleId,
            memberName: `${targetPerson.firstName} ${targetPerson.lastName}`,
            memberId: targetPerson.memberId || '',
            personId,
            personOrgRoleId: targetRole.personOrgRoleId,
            personOrgRoleDurationId,
            representativeTypeOrgRoleFieldId: representativeTypeValues?.orgRoleFieldId || Constants.DEFAULT_ID,
            termLengthOrgRoleFieldId: termLengthValues?.orgRoleFieldId || Constants.DEFAULT_ID
          });
        }
      }
    }
    else {
      onBackClicked();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memberAdministrationHQCommitteeState.isSaved === true) {
      navigate(navLinks.HQ_COMMITTEE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberAdministrationHQCommitteeState.isSaved]);

  function submitFormCallback() {
    const existingPersonOrgRoles = getExistingPersonOrgRoles(viewState.personId);

    if (existingPersonOrgRoles) {
      if (isDurationValid(viewState.personOrgRoleId, viewState.personOrgRoleDurationId, formState.effectiveDate, formState.expirationDate, existingPersonOrgRoles)) {
        putHQCommitteeMember(USAS_ORG_UNIT_ID, viewState.personId, HQ_COMMITTEE_ROLE_GROUP_ID, createStaffObject(formState, viewState, existingPersonOrgRoles));
      } else {
        const overlap = getOverlappingDates(viewState.personOrgRoleId, viewState.personOrgRoleDurationId, formState.effectiveDate, formState.expirationDate, existingPersonOrgRoles);
        setErrors({
          multipleActiveRoles: `This member has an existing ${viewState.orgRoleName} role from ${formatDate(overlap?.effectiveDate)} - ${formatDate(overlap?.expirationDate)}. Date overlaps are not allowed.`
        });
      }
    }
  };

  function getExistingPersonOrgRoles(personId) {
    const existingPerson = memberAdministrationHQCommitteeState.arrayData.find(x => x.personId === personId);

    if (existingPerson && Array.isArray(existingPerson.orgRoles)) {
      return existingPerson.orgRoles;
    } else {
      return null;
    }
  };

  function isDurationValid(personOrgRoleId, personOrgRoleDurationId, effectiveDate, expirationDate, orgRoles) {
    let overlappingDate = false;

    for (const role of orgRoles) {
      if (role.personOrgRoleId === personOrgRoleId && role.personOrgRoleDurationId !== personOrgRoleDurationId) {
        if (new Date(effectiveDate) <= new Date(role.expirationDate)
          && new Date(expirationDate) >= new Date(role.effectiveDate)) {
          overlappingDate = true;
          break;
        }
      }
    }

    return !overlappingDate;
  };

  function getOverlappingDates(personOrgRoleId, personOrgRoleDurationId, effectiveDate, expirationDate, orgRoles) {
    let overlappingDuration = {};

    for (const role of orgRoles) {
      if (role.personOrgRoleId === personOrgRoleId && role.personOrgRoleDurationId !== personOrgRoleDurationId) {
        if (new Date(effectiveDate) <= new Date(role.expirationDate)
          && new Date(expirationDate) >= new Date(role.effectiveDate)) {
          overlappingDuration = role;
          break;
        }
      }
    }

    return overlappingDuration;
  };

  function createStaffObject(formState, viewState, existingPersonOrgRoles) {
    const filteredPersonOrgRoles = [];

    if (Array.isArray(existingPersonOrgRoles)) {
      for (const role of existingPersonOrgRoles) {
        if (role.isCurrent === true && role.personOrgRoleDurationId !== viewState.personOrgRoleDurationId) {
          filteredPersonOrgRoles.push({
            orgRoleId: role.orgRoleId,
            orgRoleName: role.orgRoleName,
            personOrgRoleId: role.personOrgRoleId,
            personOrgRoleDurationId: role.personOrgRoleDurationId
          });
        }
      }
    }

    return {
      personId: viewState.personId,
      personOrgRole: [
        {
          orgRoleId: viewState.orgRoleId,
          orgRoleName: viewState.orgRoleName,
          expirationDate: formState.expirationDate,
          effectiveDate: formState.effectiveDate,
          personOrgRoleId: viewState.personOrgRoleId,
          personOrgRoleDurationId: viewState.personOrgRoleDurationId,
          personOrgRoleFields: [
            {
              fieldName: ORG_ROLE_FIELD_TERM_LENGTH_FIELD_NAME,
              orgRoleFieldId: viewState.termLengthOrgRoleFieldId,
              fieldValue: formState.termLengthName
            },
            {
              fieldName: ORG_ROLE_FIELD_REPRESENTATIVE_TYPE_FIELD_NAME,
              orgRoleFieldId: viewState.representativeTypeOrgRoleFieldId,
              fieldValue: formState.representativeTypeName
            }
          ]
        },
        ...filteredPersonOrgRoles
      ]
    };
  };

  function getOrgRoleFieldValues(orgRoleFieldsArray, orgRoleFieldName) {
    if (Array.isArray(orgRoleFieldsArray) && orgRoleFieldsArray.length > 0 && orgRoleFieldName) {
      const targetOrgRoleField = orgRoleFieldsArray.find(x => x.fieldName === orgRoleFieldName);

      if (targetOrgRoleField && targetOrgRoleField.orgRoleFieldId) {
        return { orgRoleFieldId: targetOrgRoleField.orgRoleFieldId, fieldValue: targetOrgRoleField.fieldValue };
      }
    }

    return null;
  };

  function getInitialFormState() {
    return {
      effectiveDate: Constants.BLANK_DATE_STRING,
      expirationDate: Constants.BLANK_DATE_STRING,
      representativeTypeId: Constants.DEFAULT_ID,
      representativeTypeName: '--',
      termLengthId: Constants.DEFAULT_ID,
      termLengthName: '--'
    };
  };

  function getInitialViewState() {
    return {
      orgRoleName: '',
      orgRoleId: '',
      memberName: '',
      memberId: '',
      personId: '',
      personOrgRoleId: Constants.DEFAULT_ID,
      personOrgRoleDurationId: Constants.DEFAULT_ID,
      representativeTypeOrgRoleFieldId: Constants.DEFAULT_ID,
      termLengthOrgRoleFieldId: Constants.DEFAULT_ID
    };
  };

  return {
    isLoading: memberAdministrationHQCommitteeState.isArrayLoading || environmentVariableState.isLoading || isSubmitting,
    isSaving: memberAdministrationHQCommitteeState.isSaving,
    viewState,
    formState,
    errorState,
    onFormValueChanged,
    onValueTextPairChanged,
    handleSubmit,
    onBackClicked,
    representativeTypeOptions,
    termLengthOptions,
    onSaveClicked
  };
};

export default useEditHQCommitteeMember;